/**
 * Step 5: Review & Create
 *
 * Review all dataset configuration and submit creation
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { useDatasetCreationStore } from '@/features/datasets/store/datasetCreationStore';
import { validateDatasetCreation } from '@/features/datasets/utils/datasetValidation';
import { useToast } from '@/hooks/useToast';
import { useCreateDataset } from '@/hooks/useDatasets';
import { COMPRESSION_CODECS, DATASET_FORMATS, DATASET_TYPES } from '@/features/datasets/types/dataset.types';
import type { CreateDatasetRequest } from '@/features/datasets/types/dataset.types';

/**
 * Review Step Component
 */
export const DatasetReviewStep: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const store = useDatasetCreationStore();
  const createDataset = useCreateDataset();

  // Validate everything
  const validation = validateDatasetCreation({
    name: store.name,
    sourceId: store.sourceId,
    partitions: store.partitions,
    metadata: store.metadata,
  });

  /**
   * Get display label for format
   */
  const getFormatLabel = (value: string) => {
    return DATASET_FORMATS.find((f) => f.value === value)?.label || value;
  };

  /**
   * Get display label for type
   */
  const getTypeLabel = (value: string) => {
    return DATASET_TYPES.find((t) => t.value === value)?.label || value;
  };

  /**
   * Get display label for compression
   */
  const getCompressionLabel = (value: string) => {
    return COMPRESSION_CODECS.find((c) => c.value === value)?.label || value;
  };

  /**
   * Handle create dataset
   */
  const handleCreate = async () => {
    if (!validation.isValid) {
      toast({
        title: 'Validation Error',
        description: 'Please fix all validation errors before creating the dataset.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Get creation data from store
      const creationData = store.getCreationData();

      // Generate location from source or use a default
      const location = store.sourceId
        ? `source://${store.sourceId}/${creationData.name}`
        : `lakehouse://datasets/${creationData.name}`;

      // Create request payload
      const payload: CreateDatasetRequest = {
        name: creationData.name,
        qualified_name: `default.${creationData.name}`, // Generate qualified name
        type: creationData.type,
        location: location,
        format: creationData.format,
        compression: creationData.compression,
        schema_id: creationData.schemaId,
        partitions: creationData.partitions,
        tags: [], // Empty for now
        metadata: creationData.metadata,
        description: creationData.description || undefined,
      };

      // Call API to create dataset
      await createDataset.mutateAsync(payload);

      toast({
        title: 'Dataset Created',
        description: `Dataset "${creationData.name}" has been created successfully.`,
      });

      // Reset store and navigate back
      store.reset();
      navigate('/datasets');
    } catch (error) {
      toast({
        title: 'Creation Failed',
        description: error instanceof Error ? error.message : 'Failed to create dataset',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-4">Review & Create</h2>
        <p className="text-muted-foreground text-sm">
          Review your dataset configuration before creating it.
        </p>
      </div>

      {/* Validation Status */}
      {validation.isValid ? (
        <Alert>
          <CheckCircle2 className="h-4 w-4 text-success" />
          <AlertDescription className="text-success">
            All validation checks passed. Ready to create dataset.
          </AlertDescription>
        </Alert>
      ) : (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <p className="font-semibold mb-2">Validation errors found:</p>
            <ul className="list-disc list-inside space-y-1">
              {validation.errors.map((error, index) => (
                <li key={index}>
                  {error.field && <strong>{error.field}:</strong>} {error.message}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Basic Information */}
      <Card className="p-4">
        <h3 className="font-semibold mb-3">Basic Information</h3>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Name:</dt>
            <dd className="font-medium">{store.name || 'Not set'}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Description:</dt>
            <dd className="font-medium text-right max-w-md">
              {store.description || 'No description'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Format:</dt>
            <dd>
              <Badge variant="secondary">{getFormatLabel(store.format)}</Badge>
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Type:</dt>
            <dd>
              <Badge variant="outline">{getTypeLabel(store.type)}</Badge>
            </dd>
          </div>
        </dl>
      </Card>

      {/* Configuration */}
      <Card className="p-4">
        <h3 className="font-semibold mb-3">Configuration</h3>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Compression:</dt>
            <dd>
              <Badge variant="secondary">{getCompressionLabel(store.compression)}</Badge>
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Source:</dt>
            <dd className="font-medium">{store.sourceId || 'None'}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Schema:</dt>
            <dd className="font-medium">{store.schemaId || 'None'}</dd>
          </div>
        </dl>
      </Card>

      {/* Partitions */}
      <Card className="p-4">
        <h3 className="font-semibold mb-3">
          Partitions ({store.partitions.length})
        </h3>
        {store.partitions.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {store.partitions.map((partition) => (
              <Badge key={partition.id} variant="outline">
                {partition.name}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm text-destructive">⚠️ At least 1 partition required</p>
        )}
      </Card>

      {/* Metadata */}
      <Card className="p-4">
        <h3 className="font-semibold mb-3">
          Metadata ({store.metadata.length})
        </h3>
        {store.metadata.length > 0 ? (
          <dl className="space-y-2 text-sm max-h-64 overflow-y-auto">
            {store.metadata.map((entry) => (
              <div key={entry.id} className="flex justify-between gap-4">
                <dt className="text-muted-foreground font-mono">{entry.key}:</dt>
                <dd className="font-medium text-right">{entry.value}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="text-sm text-muted-foreground">No metadata entries</p>
        )}
      </Card>

      {/* Create Button */}
      <div className="flex justify-end pt-4">
        <Button
          onClick={handleCreate}
          disabled={!validation.isValid || createDataset.isPending}
          size="lg"
        >
          {createDataset.isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Creating Dataset...
            </>
          ) : (
            'Create Dataset'
          )}
        </Button>
      </div>
    </div>
  );
};
